import { useEffect, useState } from 'react';
import { Config } from '@vm/omega-shared-component-webapp';
import { trackPageView } from './trackPageView';
import { ProductSummaryType } from '../../types/types';

/**
 * possible 'path' names. The 2 webapps don't have many pages in terms of url changes. So soft page names are defined here for the
 * purposes of analytics
 *
 * Notional Page & 'Soft' page name (i.e. sent to content square)
 *
 * Page                                       | URL                            | Content Square page name
 * -----------------------------------------------------------------------------------------------------
 * Marketplace Landing Page                   | /marketplace                   | /marketplace
 * Product Page                               | /product?payload=x             | /marketplace/product/<product-name>
 * Product Application Page                   | /product-application?payload=x | /product-application/<product-name>/page-<page-number>
 * Product Application Submitted              | /product-application?payload=x | /product-application/<product-name>/submitted
 * Session Expired                            | N/A                            | /session-expired
 * Page not found                             | N/A                            | /page-not-found
 * Marketplace Landing Page Data Error        | /marketplace                   | /marketplace/product-list-error
 * Marketplace Product Page Data Error        | /product                       | /marketplace/product-data-error
 * Product Application Form Data Error        | /product-application           | /product-application/form-error
 * Product Application Form Submit Error      | /product-application           | /product-application/<product-name>/submit-error
 * Product Application Form Duplicate         | /product-application           | /product-application/duplicate
 */

export const useContentSquare = (location, productData: ProductSummaryType) => {
  const [pathState, setPath] = useState<string>();

  const currentPath = location?.pathname;
  const isMarketplacePage = currentPath === Config.get('paths.marketplace.root');
  const isProductPage = currentPath === Config.get('paths.marketplace.product');

  const setPageView = path => {
    if (!pathState || path !== pathState) {
      setPath(path);
      trackPageView(path);
    }
  };

  useEffect(() => {
    if (isMarketplacePage || isProductPage) {
      const { productListReady, productListError, productDataReady, productDataError, productSummary } = productData;
      const productTitle = productSummary?.productTitle;

      // Wait on data is being ready and not failed.
      if (!productListReady && !productListError) return;

      // If the product list call has failed
      if (productListError) {
        setPageView('/marketplace/product-list-error'); // path name to indicate error scenario
        return;
      }

      if (productDataError) {
        setPageView(`${currentPath}/${productTitle}/product-data-error`);
        return;
      }

      // Product Detail journey
      if (isProductPage) {
        if (productListReady && !productDataReady) return;

        const productTitle = productSummary?.productTitle;

        setPageView(`${currentPath}/${productTitle}`); // page name is represented by /marketplace/product/<product-title>
        return;
      } else {
        setPageView(`${currentPath}`);
      }
    } else {
      // this branch for 404
      setPageView('/page-not-found');
    }
  }, [currentPath, productData?.productListReady, productData?.productListError, productData?.productDataReady, productData?.productDataError]);
};
